import { useCallback, useMemo, useState } from 'react';
import { Switch, TextInput } from '@cbhq/cds-web/controls';
import { Icon, NavigationIcon } from '@cbhq/cds-web/icons';
import { Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Link, TextBody, TextHeadline, TextLabel1 } from '@cbhq/cds-web/typography';

import { BaseL3ConfigEnum } from ':cloud/constants';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useValidateToken } from ':cloud/queries/Base/useValidateToken';
import {
  AMAZON_S3_LINK,
  CELESTIA_LINK,
  EIGENDA_LINK,
  MODIFIED_OP_FRAMEWORK_LINK,
  OP_FRAMEWORK_LINK,
} from ':cloud/utils/links';
import { isValidContractAddress } from ':cloud/utils/paymaster';
import { BaseL3DeploymentCard } from ':cloud/widgets/base/BaseL3DeploymentCard';
import { BaseL3DeploymentSidebar } from ':cloud/widgets/base/BaseL3DeploymentSidebar';
import {
  BaseL3ValidationItem,
  BaseL3ValidationItemProps,
} from ':cloud/widgets/base/BaseL3ValidationItem';
import { Layout } from ':cloud/widgets/layout';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';

export function BaseL3Deployment() {
  const [rollupName, setRollupName] = useState('');
  const [feeRecipient, setFeeRecipient] = useState('');
  const [stack, setStack] = useState(BaseL3ConfigEnum.Default);
  const [network, setNetwork] = useState(BaseL3ConfigEnum.Testnet);
  const [dataOption, setDataOption] = useState(BaseL3ConfigEnum.Eigen);
  const [tokenAddress, setTokenAddress] = useState('');
  const [isTokenEnabled, setIsTokenEnabled] = useState(false);

  const isQueryEnabled = !!tokenAddress?.length && isValidContractAddress(tokenAddress);

  const { validations, isLoading } = useValidateToken({
    tokenAddress,
    network: network === BaseL3ConfigEnum.Testnet ? 'base-sepolia' : 'base',
    isEnabled: isQueryEnabled,
  });

  const { isDesktop } = useSimpleBreakpoints();

  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  const handleTokenAddressChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTokenAddress(e.target.value);
  }, []);

  const handleRollupNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRollupName(e.target.value);
  }, []);

  const handleFeeRecipientChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFeeRecipient(e.target.value);
  }, []);

  const handleIsTokenEnabledChange = useCallback(() => {
    setIsTokenEnabled((prev) => !prev);
  }, []);

  const validationItems: BaseL3ValidationItemProps[] = useMemo(() => {
    return [
      {
        label: 'Must be a valid ERC-20 token',
        isLoading: isLoading && isQueryEnabled,
        isValid: validations?.isErc20,
      },
      {
        label: 'Must exist on Base Sepolia',
        isLoading: isLoading && isQueryEnabled,
        isValid: validations?.hasCode,
      },
      {
        label: 'Must use a value of 18 for decimals',
        isLoading: isLoading && isQueryEnabled,
        isValid: validations?.decimalsValid,
      },
    ];
  }, [
    isLoading,
    isQueryEnabled,
    validations?.decimalsValid,
    validations?.hasCode,
    validations?.isErc20,
  ]);

  return (
    <CloudErrorBoundary name="base-l3">
      <Layout enableMaxWidth={false}>
        <Layout.MainContainer spacingHorizontal={0} spacingTop={0} spacingBottom={0}>
          <VStack width="100%" minWidth="100%" flexGrow={1} spacing={5} gap={10}>
            <TextInput
              label="L3 name"
              helperText="Identifier used in your RPC and explorer URL"
              onChange={handleRollupNameChange}
              value={rollupName}
              placeholder="e.g. blocklords-network"
            />
            <TextInput
              label="Fee recipient"
              helperText="Sequencer funds will be sent to this address"
              onChange={handleFeeRecipientChange}
              value={feeRecipient}
              placeholder="e.g. 0x..."
            />
            <VStack gap={4}>
              <TextHeadline as="h1">Stack</TextHeadline>
              <Grid gap={1} templateColumns={responsiveTemplateColumns}>
                <BaseL3DeploymentCard
                  title={BaseL3ConfigEnum.Default}
                  onPress={setStack}
                  selected={stack}
                  tagElement={<Tag colorScheme="yellow">Fast</Tag>}
                >
                  <VStack gap={1}>
                    <HStack alignItems="center" gap={2}>
                      <NavigationIcon name="rosettaProduct" />
                      {/* TODO: update to Base after mainnet launch */}
                      <TextBody as="p" color="foregroundMuted">
                        Settle on Base Sepolia
                      </TextBody>
                    </HStack>
                    <HStack alignItems="center" gap={2}>
                      <NavigationIcon name="developerAPIProduct" />
                      <TextBody as="p" color="foregroundMuted">
                        Built on{' '}
                        <Link openInNewWindow underline={false} to={MODIFIED_OP_FRAMEWORK_LINK}>
                          Modified OP Framework
                        </Link>
                        . Post data on{' '}
                        <Link openInNewWindow underline={false} to={AMAZON_S3_LINK}>
                          S3
                        </Link>
                        .
                      </TextBody>
                    </HStack>
                  </VStack>
                </BaseL3DeploymentCard>
                <BaseL3DeploymentCard
                  title={BaseL3ConfigEnum.Custom}
                  onPress={setStack}
                  selected={stack}
                  disabled
                  tagElement={<Tag colorScheme="gray">Coming soon</Tag>}
                >
                  <VStack gap={1}>
                    <HStack alignItems="center" gap={2}>
                      <NavigationIcon name="rosettaProduct" />
                      <TextBody as="p" color="foregroundMuted">
                        Settle on Base
                      </TextBody>
                    </HStack>
                    <HStack alignItems="center" gap={2}>
                      <NavigationIcon name="developerAPIProduct" />
                      <TextBody as="p" color="foregroundMuted">
                        Built on{' '}
                        <Link openInNewWindow underline={false} to={OP_FRAMEWORK_LINK}>
                          OP Framework
                        </Link>
                        . Post data on{' '}
                        <Link openInNewWindow underline={false} to={EIGENDA_LINK}>
                          Eigen
                        </Link>{' '}
                        or{' '}
                        <Link openInNewWindow underline={false} to={CELESTIA_LINK}>
                          Celestia
                        </Link>
                        .
                      </TextBody>
                    </HStack>
                  </VStack>
                </BaseL3DeploymentCard>
              </Grid>
            </VStack>
            {stack === BaseL3ConfigEnum.Custom && (
              <VStack gap={4}>
                <TextHeadline as="h1" color="foregroundMuted">
                  Select data availability option
                </TextHeadline>
                <Grid gap={1} templateColumns={responsiveTemplateColumns}>
                  <BaseL3DeploymentCard
                    title={BaseL3ConfigEnum.Eigen}
                    onPress={setDataOption}
                    selected={dataOption}
                  />
                  <BaseL3DeploymentCard
                    title={BaseL3ConfigEnum.Celestia}
                    onPress={setDataOption}
                    selected={dataOption}
                  />
                </Grid>
              </VStack>
            )}
            <VStack gap={4}>
              <TextHeadline as="h1">Network</TextHeadline>
              <Grid gap={1} templateColumns={responsiveTemplateColumns}>
                <BaseL3DeploymentCard
                  title={BaseL3ConfigEnum.Testnet}
                  onPress={setNetwork}
                  selected={network}
                  description="Deploy your L3 on Base Sepolia. Best used as a testing environment before deploying to Mainnet."
                  tagElement={<Tag colorScheme="gray">$50/month</Tag>}
                />
                <BaseL3DeploymentCard
                  title={BaseL3ConfigEnum.Mainnet}
                  onPress={setNetwork}
                  selected={network}
                  disabled
                  description="Deploy your L3 on Base Mainnet. Best used as a production environment with real economic value."
                  tagElement={<Tag colorScheme="gray">Coming soon</Tag>}
                />
              </Grid>
            </VStack>

            <VStack gap={4}>
              <HStack justifyContent="space-between">
                <HStack alignItems="center" gap={1}>
                  <TextHeadline as="h1">Custom gas token</TextHeadline>
                  <Tooltip content="The ERC-20 token that your users can use to pay for gas.">
                    <Icon name="info" size="s" color="foreground" />
                  </Tooltip>
                </HStack>
                <Switch onChange={handleIsTokenEnabledChange} checked={isTokenEnabled} />
              </HStack>
              {isTokenEnabled && (
                <VStack gap={6} spacingBottom={10}>
                  <TextInput
                    label="Token address"
                    placeholder="e.g. 0x..."
                    onChange={handleTokenAddressChange}
                    value={tokenAddress}
                  />
                  <VStack gap={3}>
                    <TextLabel1 as="label">
                      Make sure that the selected token meets the following requirements:{' '}
                    </TextLabel1>
                    {validationItems?.map((item) => <BaseL3ValidationItem {...item} />)}
                  </VStack>
                </VStack>
              )}
            </VStack>
          </VStack>
        </Layout.MainContainer>
        <Layout.SidebarContainer>
          <BaseL3DeploymentSidebar
            rollupName={rollupName}
            stack={stack}
            network={network}
            dataOption={dataOption}
            feeRecipient={feeRecipient}
            tokenAddress={tokenAddress}
            isCustomTokenEnabled={isTokenEnabled}
            isCustomTokenValidated={
              !isTokenEnabled ||
              (validations?.decimalsValid && validations?.hasCode && validations?.isErc20)
            }
          />
        </Layout.SidebarContainer>
      </Layout>
    </CloudErrorBoundary>
  );
}
